import { useCallback, useEffect, useRef, useState } from "react";
import { getAdminProfile } from "../../features/adminAuth/adminAuthStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

const TABS = [
  { key: "acceptable-use", label: "Acceptable Use Policy" },
  { key: "terms", label: "Terms & Conditions" },
  { key: "privacy", label: "Privacy Policy" },
];

const DEFAULT_TITLES = {
  "acceptable-use": "Acceptable Use Policy",
  terms: "Terms & Conditions",
  privacy: "Privacy Policy",
};

const formatDateTime = (value) => {
  if (!value) return "Never";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "Never" : d.toLocaleString();
};

// ─── Single-policy editor panel ─────────────────────────────────────────────
const PolicyEditor = ({ policyType }) => {
  const profile = getAdminProfile();
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState(DEFAULT_TITLES[policyType]);
  const [subtitle, setSubtitle] = useState("");
  const [content, setContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success"); // "success" | "error"
  const [selectedFile, setSelectedFile] = useState(null);

  const showToast = useCallback((msg, type = "success") => {
    setToastMsg(msg);
    setToastType(type);
    setTimeout(() => setToastMsg(""), 4000);
  }, []);

  // Load policy from backend
  const loadPolicy = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/policies/${policyType}`);
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error("Invalid server response"); }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTitle(data.title || DEFAULT_TITLES[policyType]);
      setSubtitle(data.subtitle || "");
      setContent(data.content || "");
      setUpdatedAt(data.updatedAt);
      setUpdatedBy(data.updatedByEmail || "");
    } catch (err) {
      showToast("Failed to load policy: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [policyType, showToast]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  // Save policy text
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/policies/${policyType}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          subtitle,
          content,
          updatedByEmail: profile?.email || "",
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error("Invalid server response"); }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setUpdatedAt(data.updatedAt);
      setUpdatedBy(profile?.email || "");
      showToast("Policy saved successfully!", "success");
    } catch (err) {
      showToast("Save failed: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // Extract text from uploaded file
  const handleExtract = async () => {
    if (!selectedFile) return;
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await fetch(
        `${API_BASE_URL}/api/admin/policies/${policyType}/extract`,
        { method: "POST", body: formData }
      );
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error("Invalid server response — check the backend is running"); }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setContent(data.extractedText);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      showToast("Text extracted — review and click Save.", "success");
    } catch (err) {
      showToast("Extraction failed: " + err.message, "error");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="legal-editor">
      {/* Toast notification */}
      {toastMsg && (
        <div className={`legal-toast legal-toast-${toastType}`}>
          {toastType === "success" ? "✔" : "✖"} {toastMsg}
        </div>
      )}

      {/* Meta row */}
      <div className="legal-meta-row">
        <span className="legal-meta-label">Last saved</span>
        <span className="legal-meta-value">{formatDateTime(updatedAt)}</span>
        {updatedBy && (
          <>
            <span className="legal-meta-label">by</span>
            <span className="legal-meta-value">{updatedBy}</span>
          </>
        )}
        <span className={`legal-status-dot ${content ? "legal-status-live" : "legal-status-empty"}`}>
          {content ? "Live" : "Empty"}
        </span>
      </div>

      {loading ? (
        <div className="legal-loading">Loading policy…</div>
      ) : (
        <>
          {/* Fields */}
          <div className="legal-fields-grid">
            <label className="legal-field">
              <span className="legal-field-label">Page Title</span>
              <input
                type="text"
                className="legal-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Acceptable Use Policy"
              />
            </label>
            <label className="legal-field">
              <span className="legal-field-label">Subtitle / Tagline</span>
              <input
                type="text"
                className="legal-input"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Short description shown under the title"
              />
            </label>
          </div>

          {/* Content textarea */}
          <label className="legal-field legal-field-full">
            <span className="legal-field-label">
              Policy Content
              <span className="legal-char-count">{content.length.toLocaleString()} chars</span>
            </span>
            <textarea
              className="legal-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Type or paste the full policy text here, or upload a PDF/DOCX file to auto-extract the content…"
              rows={20}
            />
          </label>

          {/* File upload + extract */}
          <div className="legal-upload-bar">
            <div className="legal-upload-label">
              <span className="legal-upload-icon">📄</span>
              <span>Import from file</span>
              <span className="legal-upload-hint">PDF or DOCX — extracted text will replace the editor content</span>
            </div>
            <div className="legal-upload-controls">
              <label className="legal-file-btn" htmlFor={`file-${policyType}`}>
                {selectedFile ? selectedFile.name : "Choose PDF / DOCX"}
                <input
                  ref={fileInputRef}
                  id={`file-${policyType}`}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setSelectedFile(e.target.files[0] || null)}
                  style={{ display: "none" }}
                />
              </label>
              <button
                type="button"
                className="legal-extract-btn"
                onClick={handleExtract}
                disabled={!selectedFile || extracting}
              >
                {extracting ? "Extracting…" : "Extract Text"}
              </button>
            </div>
          </div>

          {/* Save */}
          <div className="legal-actions-row">
            <button
              type="button"
              className="legal-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Policy"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main LegalPage ─────────────────────────────────────────────────────────
const LegalPage = () => {
  const [activeTab, setActiveTab] = useState("acceptable-use");

  return (
    <section className="admin-page-card">
      <div className="legal-page-header">
        <div>
          <h2>Legal Policies</h2>
          <p>
            Write or import the content for each policy. Changes are published to
            the user-facing site immediately after saving.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="legal-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`legal-tab ${activeTab === tab.key ? "legal-tab-active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Render only the active tab's editor */}
      {TABS.map((tab) => (
        <div
          key={tab.key}
          style={{ display: activeTab === tab.key ? "block" : "none" }}
        >
          <PolicyEditor policyType={tab.key} />
        </div>
      ))}
    </section>
  );
};

export default LegalPage;
